import PropTypes from 'prop-types';
import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { MdInfo } from 'react-icons/md';

/**
 * CategoryThumbnailInfo component displays informational message about category thumbnails
 * Used in category creation and update modals to explain thumbnail usage
 * Provides consistent styling and messaging across the application
 *
 * @param {string} variant - Type of info message: 'create', 'update', or 'new'
 */
function CategoryThumbnailInfo({ variant = 'create' }) {
  const infoTextColor = useColorModeValue('black', 'white');
  const infoBlueBg = useColorModeValue('blue.50', 'blue.700');

  /**
   * Get appropriate message based on the variant
   * @param {string} messageType - The message variant type
   * @returns {string} - The formatted info message
   */
  const getMessage = (messageType) => {
    switch (messageType) {
      case 'create':
        return "The image shown above will be used as a fallback thumbnail for courses that don't have their own thumbnail image.";
      case 'update':
        return 'This is the current category thumbnail that serves as a fallback for courses without thumbnails.';
      case 'new':
        return 'The new image shown above will replace the current category thumbnail and serve as a fallback for courses without thumbnails.';
      default:
        return "This category thumbnail serves as a fallback image for courses that don't have their own thumbnail.";
    }
  };

  return (
    <Box
      p={3}
      mt={3}
      bg={infoBlueBg}
      borderRadius="md"
      border="1px solid"
      borderColor="blue.200"
    >
      <Flex gap={2} align="flex-start">
        <MdInfo
          fontSize="1.2em"
          color="#58a4d6ff"
          style={{ marginTop: '2px', flexShrink: 0 }}
        />
        <Text fontSize="xs" color={infoTextColor} lineHeight="1.4">
          <strong>Info:</strong> {getMessage(variant)}
        </Text>
      </Flex>
    </Box>
  );
}

CategoryThumbnailInfo.propTypes = {
  variant: PropTypes.oneOf(['create', 'update', 'new']),
};

CategoryThumbnailInfo.defaultProps = {
  variant: 'create',
};

export default CategoryThumbnailInfo;
